import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Dialog, DialogActions, DialogContent, Tooltip } from "@mui/material";
import DialogTitle from "apps/web/src/componentLibrary/DialogTitle";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  AthleteSummaryAthleteFragment,
  TeamWithSportFragment,
  useEditSyncedAthleteMutation,
  useRemoveAthleteSexOverrideMutation,
} from "../../../types";
import { useSnackbar } from "../../Snackbar/SnackbarContext";
import LoadingButton from "../../universal/LoadingButton";
import { AthleteForm } from "../Form";
import { AthleteFormSchema, AthleteFormType, athleteFormDefaultValues, syncedAthleteForToEditInput } from "../Form/AthleteFormSchema";
import { useTranslation } from "react-i18next";
import { useNutritionFlags } from "../../../hooks/useNutritionFlags";

interface SyncedAthleteEditModalProps {
  athlete: AthleteSummaryAthleteFragment;
  open: boolean;
  teams: readonly TeamWithSportFragment[];
  onClose: () => void;
}

const SyncedAthleteEditModal = ({ open, onClose, athlete, teams }: SyncedAthleteEditModalProps) => {
  const { t } = useTranslation();
  const { isTeamworksUserManagementEnabled } = useNutritionFlags();
  const defaultValues = useMemo(
    () => athleteFormDefaultValues(athlete.team.id, athlete, isTeamworksUserManagementEnabled),
    [athlete, athlete.team.id, isTeamworksUserManagementEnabled]
  );
  const form = useForm<AthleteFormType>({
    defaultValues,
    resolver: zodResolver(AthleteFormSchema),
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const hasFormErrors = form.formState.isSubmitted && !form.formState.isValid;

  const { setMessage } = useSnackbar();

  const [editSyncedAthlete, { loading }] = useEditSyncedAthleteMutation({
    onCompleted: data => {
      const editedAthlete = data.editSyncedAthlete.athlete;
      setMessage("success", t("athlete.syncedAthleteEditModal.success", { firstName: editedAthlete.firstName, lastName: editedAthlete.lastName }));
      onClose();
    },
    onError: () => {
      setMessage("error", t("athlete.syncedAthleteEditModal.error", { firstName: athlete.firstName, lastName: athlete.lastName }));
      onClose();
    },
  });

  const submit = (newAthlete: AthleteFormType) => {
    editSyncedAthlete({
      variables: {
        input: syncedAthleteForToEditInput(newAthlete, athlete.id, athlete.allOrderedNamedTags),
      },
    });
  };

  const [removeSexOverride] = useRemoveAthleteSexOverrideMutation({
    onCompleted: () => setMessage("success", t("athlete.form.basicInformation.sex.removeOverrideSuccess")),
    onError: () => setMessage("error", t("athlete.form.basicInformation.sex.removeOverrideError")),
  });

  const handleRemoveSexOverride = () => {
    removeSexOverride({ variables: { input: { athleteId: athlete.id } } });
  };

  return (
    <Dialog
      maxWidth={false}
      open={open}
      onClose={onClose}>
      <DialogTitle title={t("athlete.syncedAthleteEditModal.title")} onClose={onClose} />
      <DialogContent
        sx={{
          width: 800,
          display: "flex",
          flexDirection: "column",
          flexGrow: 0,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
          }}
        >
          <AthleteForm
            displayUidFields
            isForSyncedAthlete
            username={athlete.user.username ?? undefined}
            teams={teams}
            form={form}
            sexSource={athlete.sexSource}
            onRemoveSexOverride={handleRemoveSexOverride}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          variant="outlined"
          onClick={onClose}
          loading={loading}
          buttonText="Cancel" />
        <Tooltip title={hasFormErrors ? "Resolve the errors above to continue" : ""}>
          <LoadingButton
            onClick={form.handleSubmit(submit)}
            loading={loading}
            buttonText="Save" />
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SyncedAthleteEditModal;
